import type { Language } from '@eformulieren/engine'
import type { ReactNode } from 'react'
import { BubbleIcon } from '../icons/BubbleIcon.js'
import { adviceCountWord } from './adviceModel.js'

/**
 * Wat er bij een vraag of een sectiekop over advies staat (R-184, ontwerp 7.4a, mockup 01 van advies):
 * het label "1 advies" als er een verzoek aan deze plek hangt (opent het paneel op die kaarten), de
 * tekstballon om advies te vragen waar de kijker dat kan (opent de dialoog met dit anker), en op de
 * adviseurpagina de vaste tekst "Hierover gaat de vraag".
 *
 * De ballon is een knop met zijn naam in de tekst: "Advies vragen" wordt zichtbaar zodra hij de focus
 * heeft, en "over <naam>" staat er voor de schermlezer bij, zodat tien ballonnen op een pagina tien
 * verschillende namen hebben (WCAG 2.4.6). Het label noemt de plek ook ("1 advies bij <naam>"), met de
 * zichtbare tekst vooraan (WCAG 2.5.3).
 */

export interface AdviceSpotProps {
  readonly taal: Language
  /** De naam van de vraag of sectie, zoals de lezer hem ziet. */
  readonly naam: string
  readonly aantal: number
  readonly vragen: boolean
  readonly vast: boolean
  readonly onVragen: () => void
  readonly onToon: () => void
}

const T = {
  vragen: { nl: 'Advies vragen', en: 'Ask for advice' },
  over: { nl: ' over {a}', en: ' about {a}' },
  bij: { nl: ' bij {a}', en: ' on {a}' },
  vast: { nl: 'Hierover gaat de vraag', en: 'The question is about this' },
} as const

export function AdviceSpot({ taal: language, naam: name, aantal: count, vragen: canAsk, vast: fixed, onVragen, onToon }: AdviceSpotProps): ReactNode {
  if (count === 0 && !canAsk && !fixed) return null
  return (
    <div className="ef-adviesplek">
      {fixed ? <span className="ef-adviesplek__vast">{T.vast[language]}</span> : null}
      {count === 0 ? null : (
        <button type="button" className="ef-adviesplek__aantal" onClick={onToon}>
          {adviceCountWord(count, language)}
          <span className="ef-sr-only">{T.bij[language].replace('{a}', name)}</span>
        </button>
      )}
      {canAsk ? (
        <button type="button" className="ef-adviesplek__vraag" aria-haspopup="dialog" onClick={onVragen}>
          <BubbleIcon aria-hidden="true" />
          <span className="ef-adviesplek__naam">{T.vragen[language]}</span>
          <span className="ef-sr-only">{T.over[language].replace('{a}', name)}</span>
        </button>
      ) : null}
    </div>
  )
}
